// src/pages/company-head/AppSliderManagement.js
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import {
  FaSlidersH, FaPlus, FaEdit, FaTrash, FaEye, FaImage,
  FaChartBar, FaImages, FaTimes, FaCheck, FaSearch, FaSpinner
} from 'react-icons/fa';

// ─── Concept Product Picker ───────────────────────────────────────────────────
const ConceptProductPicker = ({ selected, onChange, token }) => {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/concept-products?limit=200&status=active`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.success) setProducts(res.data.data.products || res.data.data || []);
      } catch (e) {
        console.error('Failed to fetch concept products', e);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, [token]);

  const filtered = products.filter(p => {
    const name = (p.productName || p.name || '').toLowerCase();
    return name.includes(productSearch.toLowerCase());
  });

  const toggle = (id) => {
    const strId = String(id);
    if (selected.includes(strId)) onChange(selected.filter(s => s !== strId));
    else onChange([...selected, strId]);
  };

  if (loadingProducts) return (
    <div className="flex items-center justify-center py-4 text-gray-500">
      <FaSpinner className="animate-spin mr-2" /> Loading products...
    </div>
  );

  return (
    <div>
      <div className="relative mb-2">
        <FaSearch className="absolute left-2 top-2.5 text-gray-400 text-xs" />
        <input
          type="text"
          value={productSearch}
          onChange={e => setProductSearch(e.target.value)}
          placeholder="Search products..."
          className="w-full pl-6 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div className="border border-gray-200 rounded-md overflow-y-auto max-h-48">
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No products found</p>
        ) : (
          filtered.map(p => {
            const id = String(p._id);
            const isChecked = selected.includes(id);
            const name = p.productName || p.name || 'Unnamed';
            return (
              <label
                key={id}
                className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${isChecked ? 'bg-blue-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(id)}
                  className="mr-2 text-blue-600"
                />
                <span className="text-sm text-gray-800 flex-1 truncate">{name}</span>
                {isChecked && <FaCheck className="text-blue-500 text-xs flex-shrink-0" />}
              </label>
            );
          })
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">{selected.length} product(s) selected</p>
    </div>
  );
};

// ─── Slider Modal (Create / Edit) ─────────────────────────────────────────────
const SliderModal = ({ slider, onSuccess, onClose, token }) => {
  const isEdit = Boolean(slider);
  const [form, setForm] = useState({
    title: slider?.title || '',
    description: slider?.description || '',
    displayOrder: slider?.displayOrder ?? 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(slider?.imageBase64 || null);
  const [selectedProducts, setSelectedProducts] = useState(
    slider?.items?.map(i => String(i.itemId)) || []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!isEdit && !imageFile) { setError('Image is required'); return; }

    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('displayOrder', form.displayOrder);
      fd.append('items', JSON.stringify(selectedProducts));
      if (imageFile) fd.append('image', imageFile);

      const url = isEdit
        ? `${process.env.REACT_APP_API_BASE_URL}/app-sliders/web/${slider._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/app-sliders/web`;

      const res = await axios({
        method: isEdit ? 'put' : 'post',
        url,
        data: fd,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) onSuccess();
    } catch (err) {
      console.error('Save slider error:', err);
      setError(err.response?.data?.message || 'Failed to save slider');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Slider' : 'Add New Slider'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              min={0}
              value={form.displayOrder}
              onChange={e => setForm(f => ({ ...f, displayOrder: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banner Image {!isEdit && '*'}
            </label>
            {imagePreview && (
              <img src={imagePreview} alt="preview" className="w-full h-32 object-cover rounded mb-2" />
            )}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-400 rounded-md text-sm text-gray-600 hover:bg-gray-50 w-full justify-center"
            >
              <FaImage /> {imagePreview ? 'Change Image' : 'Upload Image'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </div>

          {/* Concept Products */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Linked Concept Products
            </label>
            <ConceptProductPicker
              selected={selectedProducts}
              onChange={setSelectedProducts}
              token={token}
            />
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              disabled={saving}
            >
              {saving && <FaSpinner className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Slider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AppSliderManagement = () => {
  const authContext = useContext(AuthContext);
  const { token } = authContext;
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, totalViews: 0, totalClicks: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [modalSlider, setModalSlider] = useState(null); // null = closed, false = create, slider obj = edit
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const PAGE_SIZE = 10;

  const apiHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);


  const fetchSliders = useCallback(async (search = searchTerm, page = currentPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.append('search', search);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/app-sliders/web?${params}`,
        { headers: apiHeaders() }
      );
      if (res.data.success) {
        setSliders(res.data.data.sliders);
        setPagination(res.data.data.pagination || {});
      }
    } catch (e) {
      console.error('Fetch sliders error:', e);
      setError('Failed to load sliders');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, apiHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/app-sliders/web/stats`,
        { headers: apiHeaders() }
      );
      if (res.data.success) setStats(res.data.data);
    } catch (e) {
      console.error('Fetch stats error:', e);
    }
  }, [apiHeaders]);

  const deleteSlider = async (sliderId) => {
    if (!window.confirm('Delete this slider? This cannot be undone.')) return;
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/app-sliders/web/${sliderId}`,
        { headers: apiHeaders() }
      );
      if (res.data.success) {
        setSuccess('Slider deleted');
        setSliders(prev => prev.filter(s => s._id !== sliderId));
        fetchStats();
      }
    } catch (e) {
      console.error('Delete slider error:', e);
      setError('Failed to delete slider');
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSuccess(modalSlider ? 'Slider updated successfully' : 'Slider created successfully');
    fetchSliders();
    fetchStats();
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setCurrentPage(1);
    fetchSliders(val, 1);
  };

  // Auto-clear messages
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  useEffect(() => {
    fetchSliders();
    fetchStats();
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  if (loading && sliders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-blue-500 text-3xl" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Flash messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <FaCheck /> {success}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <FaTimes /> {error}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaSlidersH className="text-blue-500" /> App Slider Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage carousel banners shown on the mobile app home screen</p>
        </div>
        <button
          onClick={() => { setModalSlider(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <FaPlus /> Add New Slider
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-lg"><FaSlidersH className="text-blue-500 text-xl" /></div>
          <div><p className="text-xs text-gray-500 uppercase tracking-wide">Total Sliders</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
          <div className="p-3 bg-purple-50 rounded-lg"><FaEye className="text-purple-500 text-xl" /></div>
          <div><p className="text-xs text-gray-500 uppercase tracking-wide">Total Views</p><p className="text-2xl font-bold text-gray-900">{stats.totalViews || 0}</p></div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
          <div className="p-3 bg-orange-50 rounded-lg"><FaChartBar className="text-orange-500 text-xl" /></div>
          <div><p className="text-xs text-gray-500 uppercase tracking-wide">Total Clicks</p><p className="text-2xl font-bold text-gray-900">{stats.totalClicks || 0}</p></div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="relative">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by title or description..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Sliders grid */}
      {sliders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100 shadow-sm">
          <FaImages className="mx-auto text-5xl text-gray-300 mb-3" />
          <h3 className="text-gray-700 font-medium">No sliders yet</h3>
          <p className="text-gray-400 text-sm mt-1">Create your first banner slider for the app</p>
          <button
            onClick={() => { setModalSlider(null); setShowModal(true); }}
            className="mt-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm mx-auto"
          >
            <FaPlus /> Add Your First Slider
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sliders.map(slider => (
            <div key={slider._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
              {/* Banner image */}
              <div className="relative h-36 bg-gray-100">
                {slider.imageBase64 ? (
                  <img src={slider.imageBase64} alt={slider.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <FaImage className="text-4xl" />
                  </div>
                )}
                <span className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-0.5 rounded-full">
                  Order: {slider.displayOrder}
                </span>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 truncate">{slider.title}</h3>
                {slider.description && (
                  <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{slider.description}</p>
                )}

                {/* Items count */}
                <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                  <FaImages />
                  <span>{slider.items?.length || 0} concept product(s) linked</span>
                </div>

                {/* Analytics */}
                <div className="mt-3 flex gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><FaEye /> {slider.viewCount || 0} views</span>
                  <span className="flex items-center gap-1"><FaChartBar /> {slider.clickCount || 0} clicks</span>
                </div>

                <p className="text-xs text-gray-400 mt-1">{formatDate(slider.createdAt)}</p>
              </div>

              {/* Actions */}
              <div className="px-4 pb-4 flex gap-2">
                <button
                  onClick={() => { setModalSlider(slider); setShowModal(true); }}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm border border-blue-300 text-blue-600 rounded-md hover:bg-blue-50"
                >
                  <FaEdit /> Edit
                </button>
                <button
                  onClick={() => deleteSlider(slider._id)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => { setCurrentPage(page); fetchSliders(searchTerm, page); }}
              className={`px-3 py-1 rounded text-sm ${currentPage === page ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <SliderModal
          slider={modalSlider}
          token={token}
          onSuccess={handleModalSuccess}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default AppSliderManagement;

